import React, { useState, useEffect } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { Card, Tag, Tooltip, Avatar, Progress, Dropdown } from "antd";
import {
  UserOutlined, PhoneOutlined, CalendarOutlined,
  PauseCircleOutlined, FireOutlined, CheckCircleOutlined, SendOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import { DirhamSymbol } from "dirham/react";
import { URGENCY_CFG } from "./pipeline";
import { getAgentColor, agentInitials, _fetchVehicleLogos, getVehicleLogoUrl } from "./helpers";
import watiLogo from "../../assets/wati_logo_only.png";

dayjs.extend(utc);

function VehicleBox({ v }) {
  const brandName = typeof v.brand_name === "string" ? v.brand_name
    : typeof v.brand === "string" ? v.brand
    : null;
  const modelName = v.model_name || v.model || null;

  const [logoUrl, setLogoUrl] = useState(() => getVehicleLogoUrl(brandName));

  useEffect(() => {
    if (logoUrl !== null || !brandName) return;
    _fetchVehicleLogos().then(() => setLogoUrl(getVehicleLogoUrl(brandName)));
  }, [brandName]); // eslint-disable-line

  if (!brandName && !modelName && !v.year) return null;

  return (
    <div style={{
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      flexShrink: 0, width: 58,
      border: "1px solid #e0e0e0", borderRadius: 8,
      padding: "5px 4px", gap: 2, minHeight: 58,
    }}>
      {logoUrl ? (
        <Tooltip title={brandName}>
          <img src={logoUrl} alt={brandName} style={{ width: 32, height: 22, objectFit: "contain" }} />
        </Tooltip>
      ) : brandName ? (
        <span style={{ fontWeight: 700, fontSize: 10, textAlign: "center", lineHeight: 1.2, wordBreak: "break-word" }}>
          {brandName}
        </span>
      ) : null}
      {modelName && (
        <span style={{ fontSize: 9, color: "#595959", textAlign: "center", lineHeight: 1.2, wordBreak: "break-word", maxWidth: 50 }}>
          {modelName}
        </span>
      )}
      {v.year && (
        <span style={{ fontSize: 9, color: "#8c8c8c" }}>{v.year}</span>
      )}
    </div>
  );
}

const urgencyMenuItems = Object.entries(URGENCY_CFG).map(([k, v]) => ({
  key: k,
  label: (
    <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <span style={{
        display: "inline-block", width: 8, height: 8, borderRadius: "50%",
        background: v.color, flexShrink: 0,
      }} />
      {v.label}
    </span>
  ),
}));

const PARKED_STATUSES = new Set(["parked", "on_hold", "postponed"]);

function ParkCountdown({ until }) {
  const [now, setNow] = useState(() => dayjs.utc());
  useEffect(() => {
    const id = setInterval(() => setNow(dayjs.utc()), 60_000);
    return () => clearInterval(id);
  }, []);

  const target = dayjs.utc(until.replace(" ", "T"));
  const diffMins = target.diff(now, "minute");
  const absDate = target.local().format("D MMM, h:mm A");

  if (diffMins > 0) {
    const h = Math.floor(diffMins / 60);
    const m = diffMins % 60;
    return { label: absDate, badge: `in ${h > 0 ? `${h}h ` : ""}${m}m`, color: "#1677ff", overdue: false };
  }
  if (diffMins === 0) {
    return { label: absDate, badge: "due now", color: "#faad14", overdue: false };
  }
  const over = Math.abs(diffMins);
  const h = Math.floor(over / 60);
  const m = over % 60;
  return { label: absDate, badge: `${h > 0 ? `${h}h ` : ""}${m}m overdue`, color: "#ff4d4f", overdue: true };
}

const TERMINAL_STATUSES = new Set(["cancelled", "completed", "disabled"]);

export const WorkflowCard = React.memo(function WorkflowCard({ card, columnColor, onCardClick, onDraftClick, onSendClick, onPhoneClick, onUrgencyChange, onPriceClick, onPaymentClick, onReassignClick, onUnparkClick, onCancelClick, sendingProgress, approvalStatus }) {
  const urgCfg = card.urgency ? URGENCY_CFG[card.urgency] : null;
  const createdDate = card.created_at ? dayjs(card.created_at) : null;
  const assignee = card.assigned_user;
  const v = card.vehicle;
  const isParked = PARKED_STATUSES.has(card.status);
  const assigneeName = assignee
    ? (assignee.name ? `${assignee.name}${assignee.surname ? " " + assignee.surname : ""}` : assignee.username)
    : null;
  const customerName = card.customer?.customer_name || card.customer_name || null;
  const customerMobile = card.customer?.customer_mobile || card.customer_mobile || null;

  // Draft cards use column color regardless of urgency (urgency not yet confirmed for drafts)
  const borderColor = card.status === "draft" ? columnColor : (urgCfg ? urgCfg.color : columnColor);

  return (
    <Card
      id={`query-card-${card.query_id}`}
      hoverable
      onClick={card.status === "draft" && onDraftClick ? () => onDraftClick(card) : onCardClick}
      size="small"
      style={{
        borderRadius: 6,
        borderLeft: `4px solid ${borderColor}`,
        backgroundColor: urgCfg ? urgCfg.cardBg : undefined,
        cursor: "pointer",
        fontSize: 12,
        transition: "background-color 0.2s, border-color 0.2s",
      }}
      styles={{ body: { padding: "8px 10px" } }}
    >
      {/* Row 1: query ID · urgency chip · date+time */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4, gap: 4 }}>
        <span style={{ fontWeight: 700, color: "#1677ff", fontSize: 12, flexShrink: 0 }}>
          #{card.query_id}
        </span>
        <div style={{ display: "flex", alignItems: "center", gap: 5, marginLeft: "auto" }}>
          {urgCfg && (
            <Dropdown
              menu={{
                items: urgencyMenuItems,
                selectedKeys: [String(card.urgency)],
                onClick: ({ key, domEvent }) => {
                  domEvent.stopPropagation();
                  const next = Number(key);
                  if (next !== card.urgency) onUrgencyChange?.(card, next);
                },
              }}
              trigger={["click"]}
              onClick={e => e.stopPropagation()}
            >
              <Tooltip title="Change urgency">
                <span style={{
                  padding: "1px 7px", borderRadius: 20, fontSize: 11, fontWeight: 600,
                  background: urgCfg.pill, color: urgCfg.color, border: `1.5px solid ${urgCfg.color}`,
                  display: "inline-flex", alignItems: "center", gap: 3, cursor: "pointer",
                }}>
                  {card.urgency === 5 && <FireOutlined style={{ fontSize: 10 }} />}
                  {urgCfg.label.split(" · ")[1]}
                </span>
              </Tooltip>
            </Dropdown>
          )}
          {createdDate?.isValid() && (
            <span style={{ color: "#8c8c8c", fontSize: 11, flexShrink: 0 }}>
              <CalendarOutlined style={{ marginRight: 2 }} />
              {createdDate.format("DD-MM-YY")}
            </span>
          )}
          {(onReassignClick || onUnparkClick || onCancelClick) && (
            <Dropdown
              trigger={["click"]}
              onClick={e => e.stopPropagation()}
              menu={{
                items: [
                  ...(onReassignClick ? [{ key: "reassign", label: "Reassign Task" }] : []),
                  ...(onUnparkClick && isParked ? [{ key: "unpark", label: "Unpark Task" }] : []),
                  ...(onCancelClick && !TERMINAL_STATUSES.has(card.status) ? [
                    { type: "divider" },
                    { key: "cancel", label: "Cancel Task", danger: true },
                  ] : []),
                ],
                onClick: ({ key, domEvent }) => {
                  domEvent.stopPropagation();
                  if (key === "reassign") onReassignClick?.(card);
                  if (key === "unpark") onUnparkClick?.(card);
                  if (key === "cancel") onCancelClick?.(card);
                },
              }}
            >
              <MoreOutlined
                onClick={e => e.stopPropagation()}
                style={{ fontSize: 14, color: "#8c8c8c", padding: "0 2px", cursor: "pointer", flexShrink: 0 }}
              />
            </Dropdown>
          )}
        </div>
      </div>

      {/* Row 2: assignee · time */}
      {(assigneeName || createdDate?.isValid()) && (
        <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 4 }}>
          {assigneeName && (
            <>
              <Avatar size={16} style={{ backgroundColor: getAgentColor(assigneeName), fontSize: 8, flexShrink: 0 }}>
                {agentInitials(assigneeName)}
              </Avatar>
              <span style={{
                fontSize: 11, fontWeight: 600, color: getAgentColor(assigneeName),
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1,
              }}>
                {assigneeName}
              </span>
            </>
          )}
          {createdDate?.isValid() && (
            <span style={{ color: "#bfbfbf", fontSize: 10, marginLeft: "auto", flexShrink: 0 }}>
              {createdDate.format("h:mm A")}
            </span>
          )}
        </div>
      )}

      {/* Row 3: vehicle box + customer box */}
      {(v || customerName || customerMobile) && (
        <div style={{ display: "flex", gap: 8, marginBottom: 4, alignItems: "stretch" }}>
          {v && <VehicleBox v={v} />}
          {(customerName || customerMobile) && (
            <div style={{
              flex: 1, minWidth: 0,
              display: "flex", flexDirection: "column", justifyContent: "center", gap: 3,
              border: "1px solid #e0e0e0", borderRadius: 8, padding: "4px 7px",
            }}>
              {customerName && (
                <div style={{ display: "flex", alignItems: "center", gap: 3, minWidth: 0 }}>
                  <UserOutlined style={{ fontSize: 10, color: "#8c8c8c", flexShrink: 0 }} />
                  <span style={{ fontSize: 11, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {customerName}
                  </span>
                </div>
              )}
              {customerMobile && (
                <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
                  <PhoneOutlined style={{ fontSize: 10, color: "#8c8c8c", flexShrink: 0 }} />
                  <span style={{ fontSize: 11, color: "#595959", whiteSpace: "nowrap", flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
                    {customerMobile}
                  </span>
                  {onPhoneClick && (
                    <Tooltip title="Open in WATI chat">
                      <img
                        src={watiLogo}
                        alt="WATI"
                        onClick={e => { e.stopPropagation(); onPhoneClick(customerMobile); }}
                        style={{ width: 13, height: 13, objectFit: "contain", cursor: "pointer", flexShrink: 0, opacity: 0.7 }}
                      />
                    </Tooltip>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Sending progress bar */}
      {sendingProgress && (
        <div style={{ marginBottom: 6 }}>
          <div style={{ fontSize: 10, color: "#1677ff", marginBottom: 2 }}>
            Sending to suppliers… {sendingProgress.sent}/{sendingProgress.total}
          </div>
          <Progress
            percent={Math.round((sendingProgress.sent / sendingProgress.total) * 100)}
            size="small"
            showInfo={false}
            strokeColor="#1677ff"
            style={{ margin: 0 }}
          />
        </div>
      )}

      {/* Row 4: parts badge + other badges + send button */}
      <div style={{ display: "flex", alignItems: "center", gap: 4, minWidth: 0 }}>
        {card.query_parts?.length > 0 && (
          <Tag style={{
            margin: 0, fontSize: 10, padding: "0 5px", lineHeight: "18px",
            flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            background: "transparent",
          }}>
            {card.query_parts.map(p => p.part_name).join(", ")}
          </Tag>
        )}
        {card.has_after_sales && (
          <Tag color="green" icon={<CheckCircleOutlined />} style={{ margin: 0, fontSize: 10, padding: "0 5px", lineHeight: "18px", flexShrink: 0 }}>
            Sale confirmed
          </Tag>
        )}
        {isParked && card.postponed_until && (() => {
          const cd = ParkCountdown({ until: card.postponed_until });
          return (
            <Tag icon={<PauseCircleOutlined />} style={{ margin: 0, fontSize: 10, padding: "0 5px", lineHeight: "18px", flexShrink: 0, borderColor: cd.color, color: cd.color, background: "transparent" }}>
              {cd.badge}
            </Tag>
          );
        })()}
        {card.status === "draft" && onSendClick && (
          <button
            onClick={e => { e.stopPropagation(); onSendClick(card); }}
            style={{
              display: "inline-flex", alignItems: "center", gap: 4, flexShrink: 0, marginLeft: "auto",
              fontSize: 11, fontWeight: 600, color: "#1677ff",
              background: "#e6f4ff", border: "1px solid #91caff",
              borderRadius: 4, padding: "2px 8px", cursor: "pointer",
              lineHeight: "18px",
            }}
          >
            <SendOutlined style={{ fontSize: 10 }} />
            Send
          </button>
        )}
        {card.status === "waiting_for_price" && onPriceClick && (
          <div style={{ position: "relative", flexShrink: 0, marginLeft: "auto" }}>
            <button
              onClick={e => { e.stopPropagation(); onPriceClick(card); }}
              style={{
                display: "inline-flex", alignItems: "center", gap: 4,
                fontSize: 11, fontWeight: 600, color: "#389e0d",
                background: "#f6ffed", border: "1px solid #b7eb8f",
                borderRadius: 4, padding: "2px 8px", cursor: "pointer",
                lineHeight: "18px",
              }}
            >
              Price
            </button>
            {card.total_parts > 0 && (
              <span style={{
                position: "absolute", top: -7, right: -7,
                background: card.parts_with_quotes >= card.total_parts ? "#52c41a" : "#faad14",
                color: "#fff", borderRadius: 8,
                padding: "0 4px", fontSize: 9, fontWeight: 700,
                lineHeight: "14px", pointerEvents: "none",
                whiteSpace: "nowrap",
              }}>
                {card.parts_with_quotes ?? 0}/{card.total_parts}
              </span>
            )}
          </div>
        )}
        {card.status === "waiting_customer_confirmation" && onPaymentClick && (
          <button
            onClick={e => { e.stopPropagation(); onPaymentClick(card); }}
            style={{
              display: "inline-flex", alignItems: "center", gap: 4, flexShrink: 0, marginLeft: "auto",
              fontSize: 11, fontWeight: 600, color: "#531dab",
              background: "#f9f0ff", border: "1px solid #d3adf7",
              borderRadius: 4, padding: "2px 8px", cursor: "pointer",
              lineHeight: "18px",
            }}
          >
            <DirhamSymbol size="11" weight="semibold" />
            Payment
          </button>
        )}
        {approvalStatus && (
          approvalStatus.status === "pending" ? (
            <Tag color="orange" style={{ fontSize: 10, margin: 0, flexShrink: 0 }}>Awaiting</Tag>
          ) : approvalStatus.status === "rejected" ? (
            <Tooltip title={approvalStatus.decision_reason ? `Rejected: ${approvalStatus.decision_reason}` : "Quote rejected"}>
              <Tag color="red" style={{ fontSize: 10, margin: 0, flexShrink: 0, cursor: "default" }}>Rejected</Tag>
            </Tooltip>
          ) : null
        )}
      </div>

      {/* Parked info block */}
      {isParked && (card.postponed_until || card.remarks) && (() => {
        const cd = card.postponed_until ? ParkCountdown({ until: card.postponed_until }) : null;
        return (
          <div style={{
            marginTop: 6, padding: "6px 8px",
            background: "#fffbe6", border: `1px solid ${cd?.overdue ? "#ffccc7" : "#ffe58f"}`, borderRadius: 6,
            fontSize: 11, color: "#595959", lineHeight: "1.5",
            display: "flex", flexDirection: "column", gap: 2,
          }}>
            {cd && (
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontWeight: 600, color: "#595959" }}>Follow-up: {cd.label}</span>
                <span style={{ fontSize: 10, fontWeight: 600, color: cd.color }}>{cd.badge}</span>
              </div>
            )}
            {card.remarks && (
              <div style={{ color: "#8c8c8c", whiteSpace: "pre-wrap" }}>{card.remarks}</div>
            )}
          </div>
        );
      })()}
    </Card>
  );
}, (prev, next) =>
  prev.card === next.card &&
  prev.columnColor === next.columnColor &&
  prev.sendingProgress === next.sendingProgress &&
  prev.onSendClick === next.onSendClick &&
  prev.onPhoneClick === next.onPhoneClick &&
  prev.onUrgencyChange === next.onUrgencyChange &&
  prev.onPriceClick === next.onPriceClick &&
  prev.onPaymentClick === next.onPaymentClick &&
  prev.approvalStatus === next.approvalStatus
);
